<ui-panel panelTitle="Staff navigation" [noGrow]="true" *ngIf="renderState as rendered">
  <div class="menu-heading">
    <h4>Workflow menu</h4>
    <button uiButton type="button" [text]="true" [outlined]="false"
      [disabled]="disabled || !menuFormatSupported || ambiguousDashboardLinks"
      (click)="restoreDefaults(rendered.origin)">
      <i class="pi pi-undo" aria-hidden="true"></i> Restore menu defaults
    </button>
  </div>
  <p *ngIf="menuReadOnlyReason" class="menu-notice" role="status">{{ menuReadOnlyReason }}</p>
  <p *ngIf="unsupportedMenu" class="hint">
    Restore menu defaults replaces the retained menu configuration only after Save draft.
  </p>
  <details *ngIf="unsupportedMenu" class="retained-menu">
    <summary>Retained menu configuration</summary>
    <pre>{{ retainedMenuJson }}</pre>
  </details>
  <div class="parent-menu-row">
    <phms-workflow-menu-icon-picker
      *ngIf="!showingRetainedMenu"
      [value]="iconKey" fallbackIcon="pi pi-sitemap" label="Workflow menu icon" [disabled]="menuDisabled"
      [active]="viewActive" [context]="pickerContext" (valueChange)="setIcon($event, rendered.origin)" />
    <span *ngIf="showingRetainedMenu" class="retained-icon">Menu icon retained</span>
    <label class="process-name">
      Process name
      <input uiControl aria-label="Process name" aria-required="true"
        [ngModel]="sharedProcessName" #processNameModel="ngModel" (ngModelChange)="updateProcessName($event, rendered.origin, processNameModel)"
        [disabled]="disabled || !processNameEditable"
        [attr.aria-invalid]="processNameError ? 'true' : null"
        [attr.aria-describedby]="processNameError ? 'staff-navigation-process-name-error' : 'staff-navigation-process-name-hint'" />
      <small id="staff-navigation-process-name-hint">Also shown in the staff menu. Shared with General.</small>
      <small *ngIf="processNameError" id="staff-navigation-process-name-error" role="alert">{{ processNameError }}</small>
      <small *ngIf="!processNameEditable && !disabled">The process name is read-only for this saved workflow format.</small>
    </label>
  </div>
  <p *ngIf="!showingRetainedMenu && !isKnownIcon(iconKey)" class="menu-notice" role="status">
    Saved workflow icon “{{ iconKey }}” is unavailable. Choose a supported icon to replace it.
  </p>

  <div class="dashboard-heading">
    <h4>Dashboard links</h4>
    <p class="hint">Access is configured on each dashboard.</p>
  </div>
  <p *ngIf="showingRetainedMenu" class="hint">Saved dashboard links are retained in the configuration above.</p>
  <div *ngIf="!showingRetainedMenu && !rows.length" class="empty-menu">
    <p>Add a dashboard from its work step to give staff a menu link here.</p>
    <button uiButton type="button" [disabled]="disabled" (click)="requestStepSetup(rendered.origin)">Open process steps</button>
  </div>
  <ng-container *ngIf="!showingRetainedMenu && rows.length && !ambiguousDashboardLinks">
    <div class="menu-table-head" aria-hidden="true">
      <span>Order</span><span>Dashboard</span><span>Menu label</span><span>Icon</span><span>Show in menu</span>
    </div>
    <ol class="dashboard-links">
      <ng-container *ngFor="let row of rows; let index = index; trackBy: trackRow">
      <li *ngIf="rowRenderState(row, rendered.origin) as rowState" class="menu-row"
        [attr.data-menu-row]="key(row)" [attr.aria-label]="rowLabel(row)">
        <div class="row-order" role="group" [attr.aria-label]="'Order ' + rowLabel(row)">
          <button uiButton type="button" class="move-button" data-move="-1"
            [attr.aria-label]="'Move ' + rowLabel(row) + ' up'" title="Move up"
            [disabled]="menuDisabled || index === 0" (click)="onReorder(index, -1, rowState)">
            <i class="pi pi-arrow-up" aria-hidden="true"></i>
          </button>
          <button uiButton type="button" class="move-button" data-move="1"
            [attr.aria-label]="'Move ' + rowLabel(row) + ' down'" title="Move down"
            [disabled]="menuDisabled || index === rows.length - 1" (click)="onReorder(index, 1, rowState)">
            <i class="pi pi-arrow-down" aria-hidden="true"></i>
          </button>
        </div>
        <div class="dashboard-context"><strong>{{ row.dashboardLabel }}</strong><small>{{ row.stepLabel }}</small></div>
        <label class="menu-label"><span class="narrow-label">Menu label</span>
          <input uiControl [attr.aria-label]="rowLabel(row) + ' menu label'" maxlength="120"
            [placeholder]="row.dashboardLabel" [ngModel]="row.label || ''" [disabled]="menuDisabled"
            #labelModel="ngModel" (ngModelChange)="updateRenderedRow(rowState, { label: $event }, labelModel)" />
        </label>
        <div class="row-icon">
          <phms-workflow-menu-icon-picker
            [value]="row.iconKey || 'pi pi-list'" [label]="rowLabel(row) + ' menu icon'"
            [disabled]="menuDisabled" [active]="viewActive" [context]="pickerContext"
            (valueChange)="updateRenderedRow(rowState, { iconKey: $event === 'pi pi-list' ? null : $event })" />
          <small *ngIf="row.iconKey && !isKnownIcon(row.iconKey)" class="unavailable-icon">Unavailable saved icon: {{ row.iconKey }}</small>
        </div>
        <label class="row-visibility">
          <input uiControl type="checkbox" [attr.aria-label]="'Show ' + rowLabel(row) + ' in menu'"
            [ngModel]="row.visible" [disabled]="menuDisabled" #visibleModel="ngModel" (ngModelChange)="updateRenderedRow(rowState, { visible: $event }, visibleModel)" />
          <span class="narrow-label">Show in menu</span>
        </label>
      </li>
      </ng-container>
    </ol>
    <p class="hint">Leave a menu label blank to use its dashboard name. Show in menu does not change dashboard access.</p>
  </ng-container>
  <p *ngIf="unresolvedCount" class="menu-notice" role="status">
    {{ unresolvedCount }} retained menu link{{ unresolvedCount === 1 ? '' : 's' }} need review. Restore menu defaults to remove missing or duplicate links.
  </p>
  <p *ngIf="menuDefaultsStaged" role="status">Menu defaults are staged. Save draft to keep this change.</p>
  <p *ngIf="error" role="alert" tabindex="-1">{{ error }}</p>
</ui-panel>
